import React, { useState, useEffect } from "react";
import { getTotalPages } from "../../util/util";
import { Modal, ModalHeader, ModalBody, ModalFooter, FilterBar } from "../../components/ui";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../services/apiClient";
import {
  Button,
  Input,
  Select,
  TextArea,
  DataTable,
  LoadingState,
  EmptyState,
  ErrorBoundary,
} from "../../components/ui";
import { useFilters } from "../../hooks";
import { CampaignCard, CampaignCardAction } from "../../components/business";
import PendingInvitationsPanel from "../../components/business/PendingInvitations/PendingInvitationsPanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable, faThLarge } from "@fortawesome/free-solid-svg-icons";
import { PageHeader, PageContainer, ContentSection } from "../../components/layout";
import type { Column } from "../../components/ui/DataTable";
import type { FilterConfig } from "../../components/ui/FilterBar/FilterItem";

interface Campaign extends Record<string, unknown> {
  id: number;
  name: string;
  setting: string;
  type: string;
  description?: string;
  world?: string;
  isPublic?: boolean;
  maxPlayers?: number;
  users?: Array<{
    id: number;
    username: string;
    name: string;
    email: string;
  }>;
}

interface CampaignFilters extends Record<string, string> {
  name: string;
  type: string;
  world: string;
}

const CampaignsPage: React.FC = () => {
  const navigate = useNavigate();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [availableTypes, setAvailableTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize filters with useFilters hook
  const { filters, updateFilter, resetFilters } = useFilters<CampaignFilters>({
    initialValues: {
      name: "",
      type: "",
      world: "",
    },
  });

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const pageSize = 10;
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "cards">("table");

  // Define filter configuration with dynamic campaign types
  const getFilterConfigs = (): FilterConfig[] => [
    {
      key: "name",
      type: "text",
      label: "Name",
      placeholder: "Search by name",
    },
    {
      key: "type",
      type: "select",
      label: "Type",
      placeholder: "Select Type",
      options: [
        { value: "", label: "All Types" },
        ...availableTypes.map((type) => ({
          value: type,
          label: type,
        })),
      ],
    },
    {
      key: "world",
      type: "text",
      label: "World",
      placeholder: "Search by world",
    },
  ];

  const fetchCampaigns = async (page: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch("/api/protected/campaigns", {
        method: "POST",
        body: JSON.stringify({
          ...filters,
          page,
          pageSize,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCampaigns(data.campaigns);
        setTotal(data.total);
        setTotalPages(getTotalPages(data.total, pageSize));

        // Extract unique campaign types for the filter
        const uniqueTypes = Array.from(
          new Set(data.campaigns.map((campaign: Campaign) => campaign.type).filter(Boolean))
        ) as string[];
        setAvailableTypes(uniqueTypes);
      } else {
        console.error("Failed to fetch campaigns");
        setError("Failed to fetch campaigns. Please try again.");
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      setError("An error occurred while loading campaigns. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filters]);

  const handleEdit = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedCampaign) return;

    try {
      const payload = {
        id: selectedCampaign.id,
        name: selectedCampaign.name,
        type: selectedCampaign.type,
        setting: selectedCampaign.setting,
        description: selectedCampaign.description || "",
        world: selectedCampaign.world || null,
        isPublic: selectedCampaign.isPublic || false,
        maxPlayers: selectedCampaign.maxPlayers || 4,
      };

      const response = await apiFetch(`/api/protected/campaigns/campaign`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        fetchCampaigns(currentPage); // Refresh campaigns
        setIsEditModalOpen(false);
      } else {
        const errorText = await response.text();
        console.error("Failed to update campaign:", errorText);
      }
    } catch (error) {
      console.error("Error updating campaign:", error);
    }
  };

  const handleView = (id: number) => {
    navigate("/protected/campaigns/" + id);
  };

  const handleCreate = () => {
    navigate("/protected/campaigns/create");
  };

  // Define table columns
  const columns: Column<Campaign>[] = [
    {
      key: "name",
      header: "Name",
      render: (campaign) => <span className="text-zinc-200 font-medium">{campaign.name}</span>,
    },
    {
      key: "type",
      header: "Type",
      render: (campaign) => campaign.type,
    },
    {
      key: "setting",
      header: "Setting",
      render: (campaign) => campaign.setting,
    },
    {
      key: "world",
      header: "World",
      render: (campaign) => campaign.world || "-",
    },
    {
      key: "players",
      header: "Players",
      render: (campaign) => {
        const playerCount = campaign.users?.length || 0;
        const maxPlayers = campaign.maxPlayers || 4;
        return `${playerCount}/${maxPlayers}`;
      },
    },
    {
      key: "isPublic",
      header: "Public",
      render: (campaign) => (campaign.isPublic ? "Yes" : "No"),
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-right",
      render: (campaign) => (
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => handleView(campaign.id)}
            className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 hover:text-amber-400 transition-colors"
          >
            View
          </button>
          <button
            onClick={() => handleEdit(campaign)}
            className="px-3 py-1.5 text-xs bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 transition-colors"
          >
            Edit
          </button>
        </div>
      ),
    },
  ];

  // Define page change handler for DataTable
  const handleDataTablePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <ErrorBoundary errorMessage="There was an error loading the campaigns page. Please try refreshing.">
      <PageContainer maxWidth="full">
        <PageHeader
          title="Chronicles"
          actions={
            <div className="flex items-center space-x-4">
              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2">
                <Button
                  variant={viewMode === "table" ? "primary" : "secondary"}
                  size="sm"
                  onClick={() => setViewMode("table")}
                  className="p-2"
                  title="Table View"
                >
                  <FontAwesomeIcon icon={faTable} />
                </Button>
                <Button
                  variant={viewMode === "cards" ? "primary" : "secondary"}
                  size="sm"
                  onClick={() => setViewMode("cards")}
                  className="p-2"
                  title="Card View"
                >
                  <FontAwesomeIcon icon={faThLarge} />
                </Button>
              </div>
              <Button variant="primary" onClick={handleCreate}>
                New Chronicle
              </Button>
            </div>
          }
        />

        <ContentSection variant="transparent">
          <PendingInvitationsPanel
            onAccepted={(campaignId) => navigate(`/protected/campaigns/${campaignId}`)}
          />
        </ContentSection>

        <ContentSection variant="transparent">
          {/* Filters */}
          <FilterBar
            filters={getFilterConfigs()}
            values={filters}
            onChange={updateFilter}
            onReset={resetFilters}
            title="Filters"
            layout="grid"
          />
        </ContentSection>

        {/* Campaign Data */}
        <ContentSection variant={viewMode === "table" ? "card" : "transparent"}>
          {loading ? (
            <LoadingState message="Loading chronicles..." size="lg" variant="spinner" />
          ) : error ? (
            <EmptyState
              title="Error Loading Campaigns"
              description={error}
              icon="error"
              size="lg"
              action={
                <Button onClick={() => fetchCampaigns(currentPage)} variant="primary">
                  Try Again
                </Button>
              }
            />
          ) : campaigns.length === 0 ? (
            <EmptyState
              title="No Chronicles Found"
              description="You haven't created any campaigns yet."
              icon="info"
              size="lg"
              action={
                <Button onClick={handleCreate} variant="primary">
                  Create Your First Chronicle
                </Button>
              }
            />
          ) : viewMode === "table" ? (
            <DataTable<Campaign>
              data={campaigns}
              columns={columns}
              onPageChange={handleDataTablePageChange}
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={total}
              pageSize={pageSize}
            />
          ) : (
            /* Campaign Cards View */
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {campaigns.map((campaign) => {
                  const campaignActions: CampaignCardAction[] = [
                    {
                      label: "View",
                      onClick: () => handleView(campaign.id),
                      variant: "primary",
                    },
                    {
                      label: "Edit",
                      onClick: () => handleEdit(campaign),
                      variant: "secondary",
                    },
                  ];

                  return (
                    <CampaignCard
                      key={campaign.id}
                      campaign={campaign}
                      actions={campaignActions}
                      variant="detailed"
                      onClick={() => handleView(campaign.id)}
                    />
                  );
                })}
              </div>

              {/* Pagination for Cards View */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-4 mt-6">
                  <Button
                    variant="secondary"
                    onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                    disabled={currentPage === 0}
                  >
                    Previous
                  </Button>
                  <span className="text-zinc-400">
                    Page {currentPage + 1} of {totalPages} ({total} campaigns)
                  </span>
                  <Button
                    variant="secondary"
                    onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                    disabled={currentPage >= totalPages - 1}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </ContentSection>

        {/* Edit Modal */}
        {isEditModalOpen && selectedCampaign && (
          <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="lg">
            <ModalHeader title="Edit Campaign" onClose={() => setIsEditModalOpen(false)} />
            <ModalBody>
              <div className="space-y-4">
                {/* Campaign Name */}
                <div>
                  <Input
                    label="Campaign Name"
                    type="text"
                    placeholder="Campaign Name"
                    value={selectedCampaign.name}
                    onChange={(e) =>
                      setSelectedCampaign((prev) =>
                        prev ? { ...prev, name: e.target.value } : prev
                      )
                    }
                    fullWidth
                  />
                </div>

                {/* Campaign Type */}
                <div>
                  <Select
                    label="Campaign Type"
                    value={selectedCampaign.type}
                    onChange={(e) =>
                      setSelectedCampaign((prev) =>
                        prev ? { ...prev, type: e.target.value } : prev
                      )
                    }
                    options={[
                      { value: "", label: "Select Type" },
                      { value: "D&D 5e", label: "D&D 5e" },
                      { value: "Pathfinder 2e", label: "Pathfinder 2e" },
                      { value: "Call of Cthulhu", label: "Call of Cthulhu" },
                      { value: "Cyberpunk 2020", label: "Cyberpunk 2020" },
                      { value: "World of Darkness", label: "World of Darkness" },
                      { value: "Custom", label: "Custom" },
                    ]}
                    fullWidth
                  />
                </div>

                {/* Campaign Setting */}
                <div>
                  <Input
                    label="Campaign Setting"
                    type="text"
                    placeholder="Campaign Setting"
                    value={selectedCampaign.setting}
                    onChange={(e) =>
                      setSelectedCampaign((prev) =>
                        prev ? { ...prev, setting: e.target.value } : prev
                      )
                    }
                    fullWidth
                  />
                </div>

                {/* World */}
                <div>
                  <Input
                    label="World (Optional)"
                    type="text"
                    placeholder="World"
                    value={selectedCampaign.world || ""}
                    onChange={(e) =>
                      setSelectedCampaign((prev) =>
                        prev ? { ...prev, world: e.target.value } : prev
                      )
                    }
                    fullWidth
                  />
                </div>

                {/* Description */}
                <div>
                  <TextArea
                    label="Description (Optional)"
                    placeholder="Campaign Description"
                    value={selectedCampaign.description || ""}
                    onChange={(e) =>
                      setSelectedCampaign((prev) =>
                        prev ? { ...prev, description: e.target.value } : prev
                      )
                    }
                    rows={3}
                    fullWidth
                  />
                </div>

                {/* Max Players and Public Settings */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Input
                      label="Max Players"
                      type="number"
                      min="1"
                      max="20"
                      value={selectedCampaign.maxPlayers || 4}
                      onChange={(e) =>
                        setSelectedCampaign((prev) =>
                          prev ? { ...prev, maxPlayers: parseInt(e.target.value) || 4 } : prev
                        )
                      }
                      fullWidth
                    />
                  </div>
                  <div className="flex items-center">
                    <Input
                      type="checkbox"
                      checked={selectedCampaign.isPublic || false}
                      onChange={(e) =>
                        setSelectedCampaign((prev) =>
                          prev ? { ...prev, isPublic: e.target.checked } : prev
                        )
                      }
                      className="mr-2"
                    />
                    <label className="text-amber-400">Public Campaign</label>
                  </div>
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setIsEditModalOpen(false)} variant="secondary" size="md">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} variant="primary" size="md">
                Save
              </Button>
            </ModalFooter>
          </Modal>
        )}
      </PageContainer>
    </ErrorBoundary>
  );
};

export default CampaignsPage;
